import { fetchMailMessage } from "@/lib/api/imap";
import {
  readMessageBodyCache,
  readMessageBodyCacheAsync,
  sanitizeMailMessageDetail,
  writeMessageBodyCache,
} from "@/lib/cache/messageBodyCache";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import { notifyBodiesWarmed } from "@/lib/mail/mailBodyWarmEvents";
import { clearBodiesWarming, markBodiesWarming } from "@/lib/mail/mailBodyWarmState";
import {
  withImapExclusiveOp,
} from "@/lib/mail/imapClientLock";
import { prefetchPreviewAttachments } from "@/lib/mail/prefetchPreviewAttachments";
import type { MailMessageDetail, MailMessageResponse } from "@/lib/types/mail";

const BODY_POLL_INTERVAL_MS = 2_000;
/** Attente DD02 / worker avant fallback IMAP (évite blocage UI). */
const BODY_POLL_MAX_MS = 45_000;
const BODY_INTERACTIVE_IMAP_TIMEOUT_MS = 120_000;
/** Prime nouveaux mails : DD02 peut annoncer prêt un peu avant le .eml lisible. */
const BODY_DD02_PRIME_RETRIES = 10;
const BODY_DD02_PRIME_GAP_MS = 280;

const inflight = new Map<string, Promise<FetchMessageBodyResult>>();

export type FetchMessageBodyResult = {
  message: MailMessageDetail | null;
  error?: string;
};

export type FetchMessageBodyOptions = {
  /** Ignore cache client et force refresh=1 côté serveur. */
  force?: boolean;
  /** Clic lecteur — pas de poll DD02 45 s, IMAP direct si cache vide. */
  interactive?: boolean;
  /**
   * Lecture DD02 seule (check=1, quelques retries) — jamais d’IMAP.
   * Pour prime nouveaux mails : zéro risque sur \Seen.
   */
  dd02Only?: boolean;
  /** Hydratation masse : 1 seul check=1, pas de retries (évite blocage minutes). */
  quick?: boolean;
  /** Timeout réseau (hydrate) — sans ça une requête pendue bloque tout le lot. */
  timeoutMs?: number;
};

function bodyKey(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isTransientFetchError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes("délai dépassé") ||
    lower.includes("connexion indisponible") ||
    lower.includes("trop de temps") ||
    lower.includes("failed to fetch")
  );
}

async function readLocalCacheSafe(
  accountId: number,
  folder: string,
  uid: number
): Promise<MailMessageDetail | null> {
  try {
    return await readMessageBodyCacheAsync(accountId, folder, uid);
  } catch {
    return null;
  }
}

async function fetchMessageOnce(
  accountId: number,
  folder: string,
  uid: number,
  refresh: boolean,
  timeoutMs?: number
): Promise<{ ok: true; data: MailMessageResponse } | { ok: false; message: string }> {
  const res = await fetchMailMessage(
    { accountId, folder, uid, refresh },
    timeoutMs != null ? { timeoutMs } : undefined
  );
  if (!res.ok) {
    return { ok: false, message: res.message };
  }
  return { ok: true, data: res.data };
}

/** Lecture DD02 seule (check=1) — pas d’attente worker 15 s ni IMAP. */
async function fetchMessageCheckOnly(
  accountId: number,
  folder: string,
  uid: number,
  timeoutMs?: number
): Promise<
  | { ok: true; data: MailMessageResponse }
  | { ok: false; message: string; transient: boolean }
> {
  const res = await fetchMailMessage(
    { accountId, folder, uid, check: true },
    timeoutMs != null && timeoutMs > 0 ? { timeoutMs } : undefined
  );
  if (!res.ok) {
    return {
      ok: false,
      message: res.message,
      transient: isTransientFetchError(res.message),
    };
  }
  return { ok: true, data: res.data };
}

/** Corps prêt mais liste PJ absente (hydrate raw avant fix meta). */
function messageNeedsAttachmentMeta(message: MailMessageDetail): boolean {
  return message.hasAttachments === true && (message.attachments?.length ?? 0) === 0;
}

function mergePreferAttachmentMeta(
  incoming: MailMessageDetail,
  existing: MailMessageDetail | null | undefined
): MailMessageDetail {
  if ((incoming.attachments?.length ?? 0) > 0) return incoming;
  const kept = existing?.attachments;
  if ((kept?.length ?? 0) > 0) {
    return {
      ...incoming,
      attachments: kept,
      hasAttachments: true,
    };
  }
  return incoming;
}

function responseHasReadableBody(data: MailMessageResponse): MailMessageDetail | null {
  const message = data.message;
  if (!message) return null;
  const sanitized = sanitizeMailMessageDetail(message);
  // Accepter tout corps lisible — ne pas rejeter pour RFC2047 sur les en-têtes
  // (sinon hit DD02 → IMAP inutile + secondes de latence).
  if (!isFetchedBodyComplete(sanitized)) {
    return null;
  }
  return sanitized;
}

async function loadFromServer(
  accountId: number,
  folder: string,
  uid: number,
  refresh: boolean,
  interactive = false
): Promise<{ ok: true; data: MailMessageResponse } | { ok: false; message: string }> {
  const imapTimeout = interactive ? BODY_INTERACTIVE_IMAP_TIMEOUT_MS : undefined;

  if (refresh) {
    return withImapExclusiveOp(() =>
      fetchMessageOnce(accountId, folder, uid, true, imapTimeout)
    );
  }

  const tryReadable = (
    res: { ok: true; data: MailMessageResponse } | { ok: false; message: string }
  ): { ok: true; data: MailMessageResponse } | null => {
    if (!res.ok) return null;
    return responseHasReadableBody(res.data) ? res : null;
  };

  const firstCheck = await fetchMessageCheckOnly(accountId, folder, uid);
  if (firstCheck.ok) {
    const hit = tryReadable(firstCheck);
    if (hit) return hit;
  } else if (!firstCheck.transient) {
    return { ok: false, message: firstCheck.message };
  }

  if (interactive) {
    // Miss DD02 net → IMAP tout de suite (pas d’attente artificielle).
    return withImapExclusiveOp(() =>
      fetchMessageOnce(accountId, folder, uid, true, imapTimeout)
    );
  }

  let lastError = firstCheck.ok ? "" : firstCheck.message;
  const deadline = Date.now() + BODY_POLL_MAX_MS;

  while (Date.now() < deadline) {
    await delay(BODY_POLL_INTERVAL_MS);
    const polled = await fetchMessageCheckOnly(accountId, folder, uid);
    if (polled.ok) {
      const hit = tryReadable(polled);
      if (hit) return hit;
    } else if (!polled.transient) {
      return { ok: false, message: polled.message };
    } else {
      lastError = polled.message;
    }
  }

  const forced = await withImapExclusiveOp(() =>
    fetchMessageOnce(accountId, folder, uid, true, imapTimeout)
  );
  if (forced.ok) {
    return forced;
  }

  if (isTransientFetchError(forced.message) || isTransientFetchError(lastError)) {
    return {
      ok: false,
      message: "Le message est en cours de préparation. Réessayez dans un instant.",
    };
  }

  return forced;
}

function extractReadableMessage(
  data: MailMessageResponse,
  bind?: { accountId: number; folder: string; uid: number }
): MailMessageDetail | null {
  const message = data.message;
  if (!message || !isFetchedBodyComplete(message)) {
    return null;
  }
  const sanitized = sanitizeMailMessageDetail(message);
  if (!isFetchedBodyComplete(sanitized)) {
    return null;
  }
  // Clé cache = params requête (pas la réponse) — évite miss lecteur si folder/uid diffèrent.
  const accountId = bind?.accountId ?? data.accountId;
  const folder = bind?.folder ?? data.folder;
  const uid = bind?.uid ?? sanitized.uid;
  let bound = uid === sanitized.uid ? sanitized : { ...sanitized, uid };
  // Ne pas écraser une bonne liste PJ locale avec un hit serveur encore vide.
  bound = mergePreferAttachmentMeta(bound, readMessageBodyCache(accountId, folder, uid));
  writeMessageBodyCache(accountId, folder, bound);
  prefetchPreviewAttachments(accountId, folder, bound);
  return bound;
}

/**
 * Flux unique : cache client → DD02 (message.php hit) → poll worker/IMAP.
 */
export function fetchMessageBodyWithCache(
  accountId: number,
  folder: string,
  uid: number,
  options: FetchMessageBodyOptions = {}
): Promise<FetchMessageBodyResult> {
  if (uid <= 0) return Promise.resolve({ message: null });

  const force = options.force === true;
  const interactive = options.interactive === true;
  const dd02Only = options.dd02Only === true;
  const quick = options.quick === true;
  const timeoutMs = options.timeoutMs;
  const key = bodyKey(accountId, folder, uid);
  const inflightKey = force ? `${key}\0force` : dd02Only ? `${key}\0dd02` : key;
  const existing = inflight.get(inflightKey);
  if (existing) return existing;

  const promise = (async (): Promise<FetchMessageBodyResult> => {
    const bind = { accountId, folder, uid };

    if (!force) {
      const local = await readLocalCacheSafe(accountId, folder, uid);
      // Afficher même si en-têtes encore MIME-encodés — le corps prime.
      if (local && isFetchedBodyComplete(local)) {
        // Hydrate masse (dd02Only) : ne pas bloquer sur meta PJ — enrichi à l’ouverture.
        if (!messageNeedsAttachmentMeta(local) || dd02Only) {
          return { message: local };
        }
        // Corps OK mais meta PJ vide (hydrate raw avant fix) → enrichir via DD02, sans IMAP.
        const enrich = await fetchMessageCheckOnly(accountId, folder, uid, timeoutMs ?? 8_000);
        if (enrich.ok) {
          const hit = responseHasReadableBody(enrich.data);
          if (hit && (hit.attachments?.length ?? 0) > 0) {
            const merged = {
              ...local,
              attachments: hit.attachments,
              hasAttachments: true,
            };
            writeMessageBodyCache(accountId, folder, merged);
            prefetchPreviewAttachments(accountId, folder, merged);
            return { message: merged };
          }
        }
        return { message: local };
      }
    }

    if (dd02Only) {
      const first = await fetchMessageCheckOnly(accountId, folder, uid, timeoutMs);
      if (first.ok) {
        const hit = extractReadableMessage(first.data, bind);
        if (hit) {
          notifyBodiesWarmed(accountId, folder, { [String(uid)]: true });
          return { message: hit };
        }
        // check=1 OK + message null = miss DD02 net
        if (quick) {
          return { message: null };
        }
      } else if (!first.transient) {
        return { message: null, error: first.message };
      } else if (quick) {
        // Timeout / réseau : ne pas confondre avec miss DD02 (sinon hydrate abandonne à vie).
        return { message: null, error: first.message };
      }
      for (let i = 0; i < BODY_DD02_PRIME_RETRIES; i++) {
        await delay(BODY_DD02_PRIME_GAP_MS);
        const retry = await fetchMessageCheckOnly(accountId, folder, uid, timeoutMs);
        if (retry.ok) {
          const hit = extractReadableMessage(retry.data, bind);
          if (hit) {
            notifyBodiesWarmed(accountId, folder, { [String(uid)]: true });
            return { message: hit };
          }
        } else if (!retry.transient) {
          return { message: null, error: retry.message };
        }
      }
      return { message: null };
    }

    // Ne pas verrouiller IMAP pendant le check DD02 — sinon l’ouverture attend l’archive.
    try {
      if (force) {
        markBodiesWarming(accountId, folder, [uid]);
        const forced = await loadFromServer(accountId, folder, uid, true, interactive);
        if (!forced.ok) {
          return { message: null, error: forced.message };
        }
        const hit = extractReadableMessage(forced.data, bind);
        if (hit) {
          notifyBodiesWarmed(accountId, folder, { [String(uid)]: true });
        }
        return hit
          ? { message: hit }
          : { message: null, error: "Contenu vide ou illisible sur le serveur." };
      }

      if (!interactive) {
        markBodiesWarming(accountId, folder, [uid]);
      }
      const loaded = await loadFromServer(accountId, folder, uid, false, interactive);
      if (!loaded.ok) {
        return { message: null, error: loaded.message };
      }

      const hit = extractReadableMessage(loaded.data, bind);
      if (hit) {
        notifyBodiesWarmed(accountId, folder, { [String(uid)]: true });
        return { message: hit };
      }

      const refreshed = await loadFromServer(accountId, folder, uid, true, interactive);
      if (!refreshed.ok) {
        return { message: null, error: refreshed.message };
      }

      const body = extractReadableMessage(refreshed.data, bind);
      if (body) {
        notifyBodiesWarmed(accountId, folder, { [String(uid)]: true });
      }
      return body
        ? { message: body }
        : { message: null, error: "Contenu vide ou illisible sur le serveur." };
    } finally {
      clearBodiesWarming(accountId, folder, [uid]);
    }
  })().finally(() => {
    inflight.delete(inflightKey);
  });

  inflight.set(inflightKey, promise);
  return promise;
}

/** Réponse / transfert : garantit un corps lisible (cache → IMAP interactif → refresh compose). */
export async function fetchMessageForCompose(
  accountId: number,
  folder: string,
  uid: number,
  existing: MailMessageDetail | null = null
): Promise<MailMessageDetail | null> {
  if (uid <= 0) return null;
  if (existing && isFetchedBodyComplete(existing)) return existing;

  const interactive = await fetchMessageBodyWithCache(accountId, folder, uid, {
    interactive: true,
  });
  if (interactive.message && isFetchedBodyComplete(interactive.message)) {
    return interactive.message;
  }

  const refreshed = await fetchMessageForComposeDraft(accountId, folder, uid);
  if (refreshed && isFetchedBodyComplete(refreshed)) return refreshed;

  return interactive.message ?? refreshed ?? existing;
}

/** Brouillon IMAP ouvrable en rédaction (corps vide autorisé). */
export function canLoadComposeDraftMessage(
  message: MailMessageDetail | null | undefined
): boolean {
  return (message?.uid ?? 0) > 0;
}

export async function fetchMessageForComposeDraft(
  accountId: number,
  folder: string,
  uid: number
): Promise<MailMessageDetail | null> {
  if (uid <= 0) return null;

  const fresh = await loadFromServer(accountId, folder, uid, true);
  if (fresh.ok) {
    const message = fresh.data.message;
    if (message && canLoadComposeDraftMessage(message)) {
      const sanitized = sanitizeMailMessageDetail(message);
      if (isFetchedBodyComplete(sanitized)) {
        writeMessageBodyCache(accountId, folder, sanitized);
      }
      return sanitized;
    }
  }

  const local = await readLocalCacheSafe(accountId, folder, uid);
  if (local && canLoadComposeDraftMessage(local)) {
    return local;
  }

  const hit = await loadFromServer(accountId, folder, uid, false);
  if (!hit.ok) return null;

  const message = hit.data.message;
  if (!message || !canLoadComposeDraftMessage(message)) return null;

  const sanitized = sanitizeMailMessageDetail(message);
  if (isFetchedBodyComplete(sanitized)) {
    writeMessageBodyCache(accountId, folder, sanitized);
  }
  return sanitized;
}

export async function primeMessageBodyFromServer(
  accountId: number,
  folder: string,
  uid: number,
  options?: {
    interactive?: boolean;
    dd02Only?: boolean;
    quick?: boolean;
    timeoutMs?: number;
  }
): Promise<boolean> {
  if (uid <= 0) return false;

  const cached = await readLocalCacheSafe(accountId, folder, uid);
  if (cached && isFetchedBodyComplete(cached)) return true;

  const hit = await fetchMessageBodyWithCache(accountId, folder, uid, {
    interactive: options?.interactive === true,
    dd02Only: options?.dd02Only === true,
    quick: options?.quick === true,
    timeoutMs: options?.timeoutMs,
  });
  return hit.message != null && isFetchedBodyComplete(hit.message);
}
